/**
 * Admin Hub Quick Stats Component
 * Dashboard cards showing key metrics
 */
import React from 'react';
import { Box, Paper, Typography, Grid, Skeleton, alpha } from '@mui/material';
import {
  Assignment as PendingIcon,
  CheckCircle as SubmittedIcon,
  Support as SupportIcon,
  TrendingUp as ActivityIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useCurrentUser, type UserInfo } from '../../hooks/useApi';
import { API_URL } from '../../utils/config';
import { ADMIN_HUB_DESIGN } from '../../config/adminHubConfig';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  href?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, loading, href, onClick }) => {
  const navigate = useNavigate();
  const isClickable = !!href || !!onClick;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (href) {
      navigate(href);
    }
  };

  return (
    <Paper
      variant="outlined"
      onClick={isClickable ? handleClick : undefined}
      sx={{
        p: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        transition: 'all 200ms ease',
        cursor: isClickable ? 'pointer' : 'default',
        '&:hover': {
          borderColor: color,
          boxShadow: `0 2px 8px ${alpha(color, 0.15)}`,
          transform: isClickable ? 'translateY(-2px)' : 'none',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 48,
          borderRadius: 2,
          backgroundColor: alpha(color, 0.1),
          color: color,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 500, mb: 0.5 }}
        >
          {title}
        </Typography>
        {loading ? (
          <Skeleton width={60} height={32} />
        ) : (
          <Typography variant="h5" fontWeight={600}>
            {value}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export const AdminHubQuickStats: React.FC = () => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { data: currentUser } = useCurrentUser() as { data: UserInfo | null | undefined };
  const userPermissions = currentUser?.permissions ?? [];

  // Check permissions for each stat
  const canViewMarketCompass = userPermissions.includes('market_compass.admin');
  const canViewSupport = userPermissions.includes('messages.all');
  const canViewSystem = userPermissions.includes('permissions.all');

  // Fetch MarketCompass dashboard counts (pending reports and submitted trend reports)
  const { data: dashboardCounts, isLoading: dashboardLoading } = useQuery({
    queryKey: ['admin-hub-stats', 'dashboard-counts'],
    queryFn: async () => {
      if (!canViewMarketCompass) return { pendingMarketReports: 0, submittedTrendReports: 0 };
      try {
        const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/dashboard-counts`);
        if (response?.ok) {
          const data = await response.json();
          return data.data ?? { pendingMarketReports: 0, submittedTrendReports: 0 };
        }
      } catch (e) {
        console.error('Failed to fetch dashboard counts:', e);
      }
      return { pendingMarketReports: 0, submittedTrendReports: 0 };
    },
    enabled: canViewMarketCompass,
    staleTime: 60000, // 1 minute
  });

  // Fetch support tickets count from admin messaging endpoint
  const { data: supportTickets, isLoading: supportLoading } = useQuery({
    queryKey: ['admin-hub-stats', 'support-tickets'],
    queryFn: async () => {
      if (!canViewSupport) return 0;
      try {
        const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/messages/threads?limit=1`);
        if (response?.ok) {
          const data = await response.json();
          // The endpoint returns stats.open which is the count of open threads
          return data.data?.stats?.open ?? 0;
        }
      } catch (e) {
        console.error('Failed to fetch support tickets count:', e);
      }
      return 0;
    },
    enabled: canViewSupport,
    staleTime: 60000,
  });

  // For "Recent Activity" we'll just show a placeholder for now
  // This could be enhanced to show actual activity metrics
  const recentActivity = '24h';

  return (
    <Grid container spacing={2}>
      {canViewMarketCompass && (
        <>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Reports"
              value={dashboardCounts?.pendingMarketReports ?? 0}
              icon={<PendingIcon />}
              color="#f57c00"
              loading={dashboardLoading}
              href="/admin/hub/marketcompass/markets"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Submitted Reports"
              value={dashboardCounts?.submittedTrendReports ?? 0}
              icon={<SubmittedIcon />}
              color="#43a047"
              loading={dashboardLoading}
              href="/admin/hub/marketcompass/reports"
            />
          </Grid>
        </>
      )}
      {canViewSupport && (
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Support Tickets"
            value={supportTickets ?? 0}
            icon={<SupportIcon />}
            color="#1976d2"
            loading={supportLoading}
            href="/admin/hub/support"
          />
        </Grid>
      )}
      {canViewSystem && (
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Recent Activity"
            value={recentActivity}
            icon={<ActivityIcon />}
            color={ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM}
            loading={false}
            href="/admin/hub/system/logs"
          />
        </Grid>
      )}
    </Grid>
  );
};

export default AdminHubQuickStats;
